// src/components/StoreList.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";

/*
 * Replace only the function keys after deploying
 * each Azure Function.
 */
const STORE_LIST_API =
    "https://oppiesfunc.azurewebsites.net/api/getPendingStores?code=9uhVXTwMgWEkWPXkPJWXouPZbDa-XhVYAVyzlLOfCUy3AzFuNP0UZQ==";

const APPROVE_API =
    "https://oppiesfunc.azurewebsites.net/api/approveStore?code=xA81-5s3OEzxDlEVp_geobp4cLI9Eczx_eLWqQrTVOG3AzFuckyeBQ==";

const REJECT_API =
    "https://oppiesfunc.azurewebsites.net/api/rejectStore?code=x_VABqEOmwR-oq3fknsWBqvskIuFBzFzQHdGeFzenH3LAzFue_yItg==";

function cleanString(value) {
    return String(
        value ?? ""
    ).trim();
}

function getStoreKey(store) {
    return [
        cleanString(store?.id),
        cleanString(store?.email)
            .toLowerCase(),
    ].join("::");
}

function getApiError(
    error,
    fallbackMessage
) {
    return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.details ||
        error?.message ||
        fallbackMessage
    );
}

function formatDate(value) {
    if (!value) {
        return "";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    return date.toLocaleString(
        "en-NZ",
        {
            dateStyle: "medium",
            timeStyle: "short",
        }
    );
}

function StoreList() {
    const [
        stores,
        setStores,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        busyKey,
        setBusyKey,
    ] = useState(null);

    const [
        status,
        setStatus,
    ] = useState({
        message: "",
        isError: false,
    });

    const [
        confirmAction,
        setConfirmAction,
    ] = useState(null);

    const [
        rejectionReason,
        setRejectionReason,
    ] = useState("");

    const endpointsConfigured =
        useMemo(
            () =>
                ![
                    STORE_LIST_API,
                    APPROVE_API,
                    REJECT_API,
                ].some((url) =>
                    url.includes(
                        "REPLACE_ME"
                    )
                ),
            []
        );

    const fetchStores =
        useCallback(async () => {
            setLoading(true);

            try {
                const response =
                    await axios.get(
                        STORE_LIST_API
                    );

                const data =
                    response.data;

                const pendingStores =
                    Array.isArray(data)
                        ? data
                        : Array.isArray(
                            data?.items
                        )
                            ? data.items
                            : [];

                setStores(
                    pendingStores
                );

                setStatus({
                    message: "",
                    isError: false,
                });
            } catch (error) {
                console.error(
                    "❌ Failed to fetch pending stores:",
                    error
                );

                setStatus({
                    message:
                        getApiError(
                            error,
                            "Failed to load pending Stores."
                        ),

                    isError: true,
                });
            } finally {
                setLoading(false);
            }
        }, []);

    useEffect(() => {
        fetchStores();
    }, [fetchStores]);

    const removeStore =
        useCallback(
            (store) => {
                const key =
                    getStoreKey(store);

                setStores(
                    (current) =>
                        current.filter(
                            (item) =>
                                getStoreKey(
                                    item
                                ) !== key
                        )
                );
            },
            []
        );

    const openApproveModal =
        useCallback((store) => {
            setRejectionReason("");

            setConfirmAction({
                type: "approve",
                store,
            });
        }, []);

    const openRejectModal =
        useCallback((store) => {
            setRejectionReason("");

            setConfirmAction({
                type: "reject",
                store,
            });
        }, []);

    const closeActionModal =
        useCallback(() => {
            if (busyKey) {
                return;
            }

            setConfirmAction(null);
            setRejectionReason("");
        }, [busyKey]);

    const approveStore =
        useCallback(
            async (store) => {
                const key =
                    getStoreKey(store);

                if (
                    !store?.id ||
                    !store?.email
                ) {
                    setStatus({
                        message:
                            "This Store record is missing its document id or email partition key.",

                        isError: true,
                    });

                    setConfirmAction(null);

                    return;
                }

                setBusyKey(key);

                try {
                    const response =
                        await axios.post(
                            APPROVE_API,
                            {
                                id:
                                    store.id,

                                storeId:
                                    store.storeId,

                                email:
                                    store.email,
                            }
                        );

                    if (
                        response?.data
                            ?.success === false
                    ) {
                        throw new Error(
                            response.data
                                .error ||
                            "Store approval failed."
                        );
                    }

                    removeStore(store);

                    setStatus({
                        message:
                            `Approved Store “${store.name ||
                            store.storeId
                            }”.`,

                        isError: false,
                    });

                    setConfirmAction(null);
                } catch (error) {
                    console.error(
                        "❌ Store approval failed:",
                        error
                    );

                    setStatus({
                        message:
                            getApiError(
                                error,
                                "Failed to approve the Store."
                            ),

                        isError: true,
                    });
                } finally {
                    setBusyKey(null);
                }
            },
            [removeStore]
        );

    const rejectStore =
        useCallback(
            async (store) => {
                const key =
                    getStoreKey(store);

                if (
                    !store?.id ||
                    !store?.email
                ) {
                    setStatus({
                        message:
                            "This Store record is missing its document id or email partition key.",

                        isError: true,
                    });

                    setConfirmAction(null);

                    return;
                }

                setBusyKey(key);

                try {
                    const response =
                        await axios.post(
                            REJECT_API,
                            {
                                id:
                                    store.id,

                                storeId:
                                    store.storeId,

                                email:
                                    store.email,

                                mode:
                                    "flag",

                                reason:
                                    cleanString(
                                        rejectionReason
                                    ) ||
                                    undefined,
                            }
                        );

                    if (
                        response?.data
                            ?.success === false
                    ) {
                        throw new Error(
                            response.data
                                .error ||
                            "Store rejection failed."
                        );
                    }

                    removeStore(store);

                    setStatus({
                        message:
                            `Rejected Store “${store.name ||
                            store.storeId
                            }”.`,

                        isError: false,
                    });

                    setConfirmAction(null);
                    setRejectionReason("");
                } catch (error) {
                    console.error(
                        "❌ Store rejection failed:",
                        error
                    );

                    setStatus({
                        message:
                            getApiError(
                                error,
                                "Failed to reject the Store."
                            ),

                        isError: true,
                    });
                } finally {
                    setBusyKey(null);
                }
            },
            [
                rejectionReason,
                removeStore,
            ]
        );

    const selectedStore =
        confirmAction?.store ||
        null;

    const selectedKey =
        selectedStore
            ? getStoreKey(
                selectedStore
            )
            : null;

    const selectedBusy =
        selectedKey &&
        busyKey === selectedKey;

    return (
        <div
            style={{
                width: "100%",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "space-between",
                    gap: 12,
                    marginBottom: 16,
                }}
            >
                <div>
                    <h2
                        style={{
                            margin: 0,
                            color: "#4c4539",
                        }}
                    >
                        Store Approvals
                    </h2>

                    <div
                        style={{
                            marginTop: 4,
                            color: "#857969",
                            fontSize: 13,
                        }}
                    >
                        Review second-hand
                        Store registrations
                        awaiting approval.
                    </div>
                </div>

                <button
                    className="btn-secondary"
                    onClick={fetchStores}
                    disabled={
                        loading ||
                        Boolean(busyKey)
                    }
                >
                    {loading
                        ? "Refreshing…"
                        : "Refresh"}
                </button>
            </div>

            {status.message && (
                <div
                    className="card"
                    style={{
                        marginBottom: 14,

                        borderColor:
                            status.isError
                                ? "#efc7c7"
                                : "#cde8d7",

                        background:
                            status.isError
                                ? "#fff6f6"
                                : "#f4fff8",
                    }}
                >
                    <div
                        style={{
                            fontWeight: 800,

                            color:
                                status.isError
                                    ? "#a94442"
                                    : "#1f5130",
                        }}
                    >
                        {status.message}
                    </div>
                </div>
            )}

            {!endpointsConfigured && (
                <div
                    className="card"
                    style={{
                        marginBottom: 14,
                        background:
                            "#fff9e6",
                        borderColor:
                            "#ead9a4",
                    }}
                >
                    <div
                        style={{
                            fontWeight: 800,
                            color: "#6b5b3a",
                        }}
                    >
                        Store approval
                        endpoints are not set
                    </div>

                    <div
                        style={{
                            marginTop: 6,
                            color: "#857969",
                            fontSize: 13,
                        }}
                    >
                        Deploy the three Azure
                        Functions and replace each
                        <code>
                            {" "}
                            REPLACE_ME{" "}
                        </code>
                        value with its Function key.
                    </div>
                </div>
            )}

            {loading ? (
                <div
                    className="card"
                    style={{
                        textAlign: "center",
                        padding: 30,
                        color: "#766e60",
                    }}
                >
                    Loading pending
                    Stores…
                </div>
            ) : stores.length ===
                0 ? (
                <div
                    className="card"
                    style={{
                        textAlign: "center",
                        padding: 30,
                    }}
                >
                    <div
                        style={{
                            fontSize: 28,
                            marginBottom: 8,
                        }}
                    >
                        ✓
                    </div>

                    <div
                        style={{
                            fontWeight: 800,
                            color: "#4c4539",
                        }}
                    >
                        No Stores awaiting
                        approval
                    </div>

                    <div
                        style={{
                            marginTop: 5,
                            color: "#857969",
                            fontSize: 13,
                        }}
                    >
                        New registrations will
                        appear here.
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",

                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(340px, 1fr))",

                        gap: 16,
                    }}
                >
                    {stores.map(
                        (store) => {
                            const key =
                                getStoreKey(
                                    store
                                );

                            const disabled =
                                busyKey === key;

                            const ownerName = [
                                store.ownerFirstName,
                                store.ownerLastName,
                            ]
                                .filter(Boolean)
                                .join(" ");

                            const submittedAt =
                                formatDate(
                                    store.submittedAt ||
                                    store.createdAt
                                );

                            return (
                                <div
                                    key={key}
                                    className="card"
                                >
                                    <div
                                        className="cardHeader"
                                    >
                                        <div
                                            style={{
                                                minWidth: 0,
                                            }}
                                        >
                                            <h3
                                                className="cardTitle"
                                                style={{
                                                    marginBottom: 3,
                                                }}
                                            >
                                                {store.name ||
                                                    "(Unnamed Store)"}
                                            </h3>

                                            <div
                                                className="cardSubtitle"
                                                style={{
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                <strong>
                                                    Store ID:
                                                </strong>{" "}
                                                {store.storeId ||
                                                    store.id}
                                            </div>
                                        </div>

                                        <span
                                            style={{
                                                flexShrink: 0,
                                                padding:
                                                    "5px 9px",
                                                borderRadius:
                                                    999,
                                                background:
                                                    "#fff3cf",
                                                color:
                                                    "#775e18",
                                                fontSize: 11,
                                                fontWeight: 800,
                                            }}
                                        >
                                            Pending
                                        </span>
                                    </div>

                                    <div
                                        className="cardBody"
                                    >
                                        {ownerName && (
                                            <p
                                                style={{
                                                    margin:
                                                        "6px 0",
                                                }}
                                            >
                                                <strong>
                                                    Owner:
                                                </strong>{" "}
                                                {ownerName}
                                            </p>
                                        )}

                                        {store.email && (
                                            <p
                                                style={{
                                                    margin:
                                                        "6px 0",
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                <strong>
                                                    Email:
                                                </strong>{" "}
                                                <a
                                                    href={`mailto:${store.email}`}
                                                >
                                                    {store.email}
                                                </a>
                                            </p>
                                        )}

                                        {store.address && (
                                            <p
                                                style={{
                                                    margin:
                                                        "6px 0",
                                                }}
                                            >
                                                <strong>
                                                    Address:
                                                </strong>{" "}
                                                {store.address}
                                            </p>
                                        )}

                                        {store.contact && (
                                            <p
                                                style={{
                                                    margin:
                                                        "6px 0",
                                                }}
                                            >
                                                <strong>
                                                    Contact:
                                                </strong>{" "}
                                                {store.contact}
                                            </p>
                                        )}

                                        {submittedAt && (
                                            <p
                                                style={{
                                                    margin:
                                                        "6px 0",
                                                    color:
                                                        "#7d7467",
                                                    fontSize: 13,
                                                }}
                                            >
                                                <strong>
                                                    Submitted:
                                                </strong>{" "}
                                                {submittedAt}
                                            </p>
                                        )}
                                    </div>

                                    <div
                                        className="cardFooter"
                                    >
                                        <button
                                            className="btn-success"
                                            onClick={() =>
                                                openApproveModal(
                                                    store
                                                )
                                            }
                                            disabled={
                                                disabled ||
                                                Boolean(
                                                    busyKey
                                                )
                                            }
                                            style={{
                                                opacity:
                                                    disabled
                                                        ? 0.7
                                                        : 1,
                                            }}
                                        >
                                            {disabled
                                                ? "Working…"
                                                : "Approve"}
                                        </button>

                                        <button
                                            className="btn-danger"
                                            onClick={() =>
                                                openRejectModal(
                                                    store
                                                )
                                            }
                                            disabled={
                                                disabled ||
                                                Boolean(
                                                    busyKey
                                                )
                                            }
                                            style={{
                                                opacity:
                                                    disabled
                                                        ? 0.7
                                                        : 1,
                                            }}
                                        >
                                            {disabled
                                                ? "Working…"
                                                : "Reject"}
                                        </button>
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>
            )}

            {confirmAction &&
                selectedStore && (
                    <div
                        role="presentation"
                        onMouseDown={
                            closeActionModal
                        }
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 1000,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "center",
                            padding: 20,
                            background:
                                "rgba(45, 40, 31, 0.58)",
                        }}
                    >
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="store-action-title"
                            onMouseDown={(
                                event
                            ) =>
                                event.stopPropagation()
                            }
                            style={{
                                width: "100%",
                                maxWidth: 440,
                                padding: 24,
                                borderRadius: 18,
                                border:
                                    "1px solid #e5dcc9",
                                background:
                                    "#fffdf8",
                                boxShadow:
                                    "0 18px 50px rgba(0,0,0,0.24)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems:
                                        "flex-start",
                                    justifyContent:
                                        "space-between",
                                    gap: 14,
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            color:
                                                confirmAction.type ===
                                                    "approve"
                                                    ? "#2e7045"
                                                    : "#a94442",

                                            fontSize: 11,
                                            fontWeight: 900,
                                            letterSpacing:
                                                0.8,
                                            textTransform:
                                                "uppercase",
                                        }}
                                    >
                                        {confirmAction.type ===
                                            "approve"
                                            ? "Approve Store"
                                            : "Reject Store"}
                                    </div>

                                    <h3
                                        id="store-action-title"
                                        style={{
                                            margin:
                                                "7px 0 0",
                                            color:
                                                "#4c4539",
                                        }}
                                    >
                                        {selectedStore.name ||
                                            selectedStore.storeId}
                                    </h3>
                                </div>

                                <button
                                    type="button"
                                    onClick={
                                        closeActionModal
                                    }
                                    disabled={
                                        selectedBusy
                                    }
                                    aria-label="Close"
                                    style={{
                                        width: 34,
                                        height: 34,
                                        border:
                                            "1px solid #ded4c1",
                                        borderRadius:
                                            "50%",
                                        background:
                                            "#ffffff",
                                        cursor:
                                            selectedBusy
                                                ? "default"
                                                : "pointer",
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div
                                style={{
                                    marginTop: 16,
                                    padding: 13,
                                    borderRadius: 11,
                                    background:
                                        "#f5f0e1",
                                    color: "#675f53",
                                    fontSize: 13,
                                    lineHeight: 1.55,
                                }}
                            >
                                <div>
                                    <strong>
                                        Email:
                                    </strong>{" "}
                                    {selectedStore.email}
                                </div>

                                <div>
                                    <strong>
                                        Document ID:
                                    </strong>{" "}
                                    {selectedStore.id}
                                </div>
                            </div>

                            {confirmAction.type ===
                                "approve" ? (
                                <p
                                    style={{
                                        margin:
                                            "17px 0 0",
                                        color: "#675f53",
                                        lineHeight: 1.55,
                                    }}
                                >
                                    This will give the
                                    Store access to its
                                    approved OPPIES Store
                                    account.
                                </p>
                            ) : (
                                <>
                                    <p
                                        style={{
                                            margin:
                                                "17px 0 9px",
                                            color:
                                                "#675f53",
                                            lineHeight:
                                                1.55,
                                        }}
                                    >
                                        The registration
                                        will be marked as
                                        rejected and
                                        removed from this
                                        pending list.
                                    </p>

                                    <label
                                        htmlFor="store-rejection-reason"
                                        style={{
                                            display: "block",
                                            marginBottom: 6,
                                            color:
                                                "#4c4539",
                                            fontSize: 13,
                                            fontWeight: 800,
                                        }}
                                    >
                                        Rejection reason
                                        (optional)
                                    </label>

                                    <textarea
                                        id="store-rejection-reason"
                                        value={
                                            rejectionReason
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setRejectionReason(
                                                event.target
                                                    .value
                                            )
                                        }
                                        disabled={
                                            selectedBusy
                                        }
                                        rows={4}
                                        placeholder="Add a note for the OPPIES Team…"
                                        style={{
                                            width: "100%",
                                            boxSizing:
                                                "border-box",
                                            resize:
                                                "vertical",
                                            padding: 11,
                                            border:
                                                "1px solid #d9cfbd",
                                            borderRadius: 10,
                                            background:
                                                "#ffffff",
                                            color:
                                                "#403b34",
                                            font:
                                                "inherit",
                                        }}
                                    />
                                </>
                            )}

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent:
                                        "flex-end",
                                    gap: 10,
                                    marginTop: 20,
                                }}
                            >
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={
                                        closeActionModal
                                    }
                                    disabled={
                                        selectedBusy
                                    }
                                >
                                    Cancel
                                </button>

                                {confirmAction.type ===
                                    "approve" ? (
                                    <button
                                        type="button"
                                        className="btn-success"
                                        onClick={() =>
                                            approveStore(
                                                selectedStore
                                            )
                                        }
                                        disabled={
                                            selectedBusy
                                        }
                                    >
                                        {selectedBusy
                                            ? "Approving…"
                                            : "Approve Store"}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-danger"
                                        onClick={() =>
                                            rejectStore(
                                                selectedStore
                                            )
                                        }
                                        disabled={
                                            selectedBusy
                                        }
                                    >
                                        {selectedBusy
                                            ? "Rejecting…"
                                            : "Reject Store"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
}

export default StoreList;